import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { backgroundClasses } from '../../leaderboard.constants';
import { ModifiedLeaderboardPlayer } from '../../leaderboard.types';
import { t } from 'i18next';

import { ROUTES } from 'shared/constants/routes';
import { useAccount } from 'shared/hooks/use-web3';
import { truncateAddress } from 'shared/utils/pretty-tx';

import CardsIcon from 'assets/svg/cards.svg?react';
import DiscordIcon from 'assets/svg/discord.svg?react';
import SwordIcon from 'assets/svg/sword.svg?react';
import UserIcon from 'assets/svg/user.svg?react';

interface ILeaderboardLineProps {
  player: ModifiedLeaderboardPlayer;
  index: number;
}

export const LeaderboardLine: FC<ILeaderboardLineProps> = ({ player, index }) => {
  const { address } = useAccount();
  const { discordUsername, walletAddress, nftsTotal, gamesTotal, gamesWon, elo, percent } = player;
  const bgClass = index > 9 ? backgroundClasses[10] : backgroundClasses[index];
  const clampedPercent = Math.max(0, Math.min(percent, 100));
  const winPercent =
    gamesTotal > 0 ? Math.min(100, Math.max(0, Math.round((gamesWon / gamesTotal) * 100))) : 0;
  const navigate = useNavigate();
  const nftHandler = () => {
    if (walletAddress)
      window.open(`https://opensea.io/${walletAddress}`, '_blank', 'noopener,noreferrer');
  };
  const isYou = useMemo(() => {
    if (!address || !walletAddress) return false;
    return address.toLowerCase() === walletAddress.toLowerCase();
  }, [address, walletAddress]);
  if (!player) return null;
  return (
    <div className="relative w-full">
      <div
        className={`absolute left-0 top-0 z-0 h-full ${isYou ? 'bg-[linear-gradient(184deg,_#B36E1B_2.5%,_#373470_95.54%)]' : bgClass}`}
        style={{ width: `${clampedPercent}%` }}
      ></div>

      <div className="relative grid w-full grid-cols-[4%_30%_12%_12%_12%_10%_20%] items-center p-2 text-sm text-white [font-variant-numeric:tabular-nums] sm:text-base">
        {/* Player */}
        <span className="shrink-0">{index + 1}</span>
        <span
          role="button"
          tabIndex={0}
          className="flex cursor-pointer items-center gap-0.5 truncate"
          onClick={nftHandler}
        >
          {discordUsername ? <DiscordIcon className="h-4" /> : <UserIcon className="h-4" />}
          <span className="truncate">
            {' '}
            {discordUsername ? `@${discordUsername}` : truncateAddress(walletAddress)}
          </span>{' '}
        </span>

        {/* NFT */}
        <span className="flex items-center gap-0.5 text-left" role="button" onClick={nftHandler}>
          {nftsTotal}
          <CardsIcon className="h-4" />
        </span>
        <span className="text-left">{gamesTotal}</span>
        <span className="text-left">{winPercent}%</span>
        <span className="text-left">{elo}</span>
        {isYou ? (
          <button
            className="justify-self-center text-[#FBD551]"
            onClick={() => {
              navigate(ROUTES.DAILY_QUESTS);
            }}
          >
            {t('leaderboard.quests')}
          </button>
        ) : (
          <button
            className="flex items-center gap-0.5 justify-self-center rounded-xl px-3 py-1 underline"
            onClick={() => {
              navigate(`${ROUTES.LOBBY}?invite=${walletAddress}`);
            }}
          >
            <SwordIcon className="h-4" />
            {t('leaderboard.duel')}
          </button>
        )}
      </div>
    </div>
  );
};
